import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button } from '@/components/ui/PageHeader';
import { providerReach, type ProviderSeason } from '@/api/provider-reach';

// ═══════════════════════════════════════════════════════════════════
// The owner's reach, below the desk's dashboard.
//
// The season: the one nudge a season. WeDesi sets which festival or occasion
// matters to which trade, with a source; the business never does. The table
// ships empty, so "no season" is the normal answer until WeDesi fills it.
// ═══════════════════════════════════════════════════════════════════

export function OwnerReachPanel({ id, categoryCode }: { id: string; categoryCode: string | undefined }) {
  return (
    <div className="space-y-6 border-t border-gray-200 pt-4 mt-6">
      <SeasonBlock id={id} categoryCode={categoryCode} />
      <AsksBlock id={id} />
      <ReceiptBlock id={id} />
    </div>
  );
}

// Asks near you: the open asks in this business's trade and towns. One
// answer each, posted as the business, shown under the members' answers.
function AsksBlock({ id }: { id: string }) {
  const [page, setPage] = useState(1);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const asks = useQuery({
    queryKey: ['provider-asks', id, page],
    queryFn: () => providerReach.asks(id, page),
    retry: false,
  });

  async function answer(questionId: string) {
    setMessage(null);
    try {
      await providerReach.answerAsBusiness(questionId, id, drafts[questionId] ?? '');
      setDrafts((old) => ({ ...old, [questionId]: '' }));
      setMessage('Answered as the business.');
      await asks.refetch();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Answer failed');
    }
  }

  return (
    <div>
      <p className="text-sm font-medium text-gray-900 mb-1">Asks near you</p>
      <p className="text-xs text-gray-500 mb-2">
        Open asks in your trade, in your town, the towns you serve, or your metro. One answer each, as the business.
      </p>
      {asks.error ? (
        <p className="text-sm text-red-700">{asks.error instanceof Error ? asks.error.message : 'Failed'}</p>
      ) : !asks.data ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : asks.data.data.items.length === 0 ? (
        <p className="text-sm text-gray-500">No open asks for your trade in your towns.</p>
      ) : (
        <ul className="space-y-2">
          {asks.data.data.items.map((a) => (
            <li key={a.id} className="border border-gray-200 rounded-xl px-4 py-3 text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-gray-900">{a.title}</span>
                <span className="text-xs text-gray-500">
                  {a.city ?? 'no town'} · {new Date(a.createdAt).toLocaleDateString()} · {a.answerCount} answers
                </span>
                {a.answeredByThisBusiness && <Badge variant="green">answered by you</Badge>}
              </div>
              {!a.answeredByThisBusiness && (
                <div className="flex gap-2 mt-2">
                  <input
                    className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                    value={drafts[a.id] ?? ''}
                    onChange={(e) => setDrafts((old) => ({ ...old, [a.id]: e.target.value }))}
                    placeholder="Answer as your business (10 characters or more)"
                  />
                  <Button size="sm" onClick={() => answer(a.id)}>Answer</Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {asks.data && asks.data.data.meta.totalPages > 1 && (
        <div className="flex gap-2 mt-2">
          <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>Newer</Button>
          <Button size="sm" variant="secondary" disabled={page >= asks.data.data.meta.totalPages} onClick={() => setPage(page + 1)}>Older</Button>
        </div>
      )}
      {message && <p className="text-sm text-gray-700 mt-2">{message}</p>}
    </div>
  );
}

function SeasonLine({ season }: { season: ProviderSeason | null }) {
  if (!season) {
    return <p className="text-sm text-gray-500">No season is set for this trade yet.</p>;
  }
  return (
    <div className="text-sm">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium text-gray-900">{season.name}</span>
        <Badge variant={season.daysAway === 0 ? 'green' : 'gray'}>
          {season.daysAway === 0 ? 'under way' : `in ${season.daysAway} days`}
        </Badge>
        <span className="text-xs text-gray-500">
          {season.startsOn}
          {season.endsOn ? ` to ${season.endsOn}` : ''} · {season.kind}
        </span>
      </div>
      <p className="text-xs text-gray-400 mt-1">Source: {season.sourceNote}</p>
    </div>
  );
}

function SeasonBlock({ id, categoryCode }: { id: string; categoryCode: string | undefined }) {
  const [tz, setTz] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [category, setCategory] = useState(categoryCode ?? '');
  const desk = useQuery({
    queryKey: ['provider-dashboard-season', id],
    queryFn: () => providerReach.dashboardSeason(id),
    retry: false,
  });
  const reference = useQuery({
    queryKey: ['provider-season', category, tz],
    queryFn: () => providerReach.season(category, tz),
    enabled: category.length > 0,
    retry: false,
  });

  return (
    <div>
      <p className="text-sm font-medium text-gray-900 mb-1">The season</p>
      <p className="text-xs text-gray-500 mb-2">On the desk, on the listing&apos;s own calendar:</p>
      {desk.data ? <SeasonLine season={desk.data.data.season} /> : <p className="text-sm text-gray-500">Loading…</p>}

      <p className="text-xs text-gray-500 mt-4 mb-1">
        Any trade, on any calendar (<code>GET /reference/provider-seasons</code>):
      </p>
      <div className="flex gap-2 flex-wrap mb-2">
        <input
          className="border border-gray-300 rounded px-2 py-1 text-sm"
          value={category}
          onChange={(e) => setCategory(e.target.value.trim())}
          placeholder="category code"
        />
        <input
          className="border border-gray-300 rounded px-2 py-1 text-sm"
          value={tz}
          onChange={(e) => setTz(e.target.value.trim())}
          placeholder="time zone"
        />
      </div>
      {reference.error ? (
        <p className="text-sm text-red-700">{reference.error instanceof Error ? reference.error.message : 'Failed'}</p>
      ) : reference.data ? (
        <>
          <p className="text-xs text-gray-400 mb-1">
            Today is {reference.data.data.today} in {reference.data.data.timeZone}.
          </p>
          <SeasonLine season={reference.data.data.season} />
        </>
      ) : null}
    </div>
  );
}

// "What WeDesi brought you": work first, then where it came from, then the
// signals, labelled as signals. Each count shows the conversations behind it.
function ReceiptBlock({ id }: { id: string }) {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const receipt = useQuery({
    queryKey: ['provider-receipt', id, month],
    queryFn: () => providerReach.receipt(id, month),
    enabled: /^\d{4}-\d{2}$/.test(month),
    retry: false,
  });
  const r = receipt.data?.data;
  const threads = (list: string[]) =>
    list.length > 0 && <span className="text-xs text-gray-400"> · conversations {list.map((t) => t.slice(0, 8)).join(', ')}</span>;

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <p className="text-sm font-medium text-gray-900">What WeDesi brought you</p>
        <input
          type="month"
          className="border border-gray-300 rounded px-2 py-1 text-sm"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        />
      </div>
      {receipt.error ? (
        <p className="text-sm text-red-700">{receipt.error instanceof Error ? receipt.error.message : 'Failed'}</p>
      ) : !r ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="text-sm space-y-1">
          <p>
            <b>{r.newPeopleInTouch.total}</b> new people got in touch: {r.newPeopleInTouch.byKind.message} messaged ·{' '}
            {r.newPeopleInTouch.byKind.call} called · {r.newPeopleInTouch.byKind.whatsapp} on WhatsApp ·{' '}
            {r.newPeopleInTouch.byKind.directions} asked the way{threads(r.newPeopleInTouch.threadIds)}
          </p>
          <p><b>{r.confirmedWork.total}</b> said the work happened{threads(r.confirmedWork.threadIds)}</p>
          <p><b>{r.cameBack.total}</b> came back{threads(r.cameBack.threadIds)}</p>
          <p className="text-xs text-gray-500 pt-1">How they found you</p>
          {r.sources.length === 0 ? (
            <p className="text-xs text-gray-400">Nobody yet.</p>
          ) : (
            <ul className="text-xs text-gray-700">
              {r.sources.map((s) => <li key={s.source}>{s.source}: {s.count}</li>)}
            </ul>
          )}
          <p className="text-xs text-gray-500 pt-1">
            Taps: {r.taps.call} call · {r.taps.whatsapp} WhatsApp · {r.taps.directions} directions · {r.taps.website} website
          </p>
          <p className="text-xs text-gray-500">
            Signals, not leads: {r.signals.pageViews} saw your page · {r.signals.newFollowers} started following
          </p>
          <p className="text-xs text-gray-400">
            Calls made from outside WeDesi can&apos;t be counted. Month on {r.timeZone}.
          </p>
        </div>
      )}
    </div>
  );
}
